import React, { useContext, useState } from 'react';
import useAnalytics from 'helpers/useAnalytics';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import _capitalize from 'lodash/capitalize';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { createDirectory } from 'store/directories';
import { createSource, initializeSource } from 'store/sources';
import makeSourceCreateBaseUrl from 'pages/Directory/helpers/makeSourceCreateBaseUrl';

// Components
import CardHeader from '@material-ui/core/CardHeader';
import SourceContext from 'helpers/SourceContext';
import TextField from '@material-ui/core/TextField';

import Base from 'pages/Directory/Base';
import {
  CHOOSE_SOURCE_TYPE_URL,
  SOURCE_WIZARD_BASE_URL,
  AUTHORIZE_DATA_ACCESS_URL,
  UPLOAD_CSV_URL,
  CSV_SOURCE_TYPE_KEY,
  DEMO_DATA_SOURCE_TYPE_KEY,
} from 'pages/Directory/consts';
import { DIRECTORIES_URL, SOURCE_URL } from 'components/Router/consts';

function NameYourData() {
  const [dataName, setDataName] = useState('');
  const classes = useStyles();
  const analytics = useAnalytics();
  const history = useHistory();
  const dispatch = useDispatch();
  const sourceContext = useContext(SourceContext);
  const { sourceInfo, setSourceInfo } = sourceContext;

  const directoryIdUrlMatch = window.location.href.match(/directories\/.*\/create-source/);
  const directoryId = directoryIdUrlMatch ? directoryIdUrlMatch[0].split('/')[1] : undefined;
  const baseUrl = makeSourceCreateBaseUrl(directoryId);
  const dataType = !directoryId ? 'primary' : 'complementary';

  const data = {
    primary: {
      title: 'Name Your Directory',
      description:
        'Give your Directory a name. This is only for your reference and should describe the set of people you’re importing. If you will only have one Directory, you could call this something like “All Employees.”',
    },
    complementary: {
      title: `Name Your ${_capitalize(dataType)} Data Source`,
      description:
        'Give your Source a name. This is only for your reference and should describe the data you’re importing. For example, if you’re bringing in Skills data from a CSV, you could call this something like “Additional Skills.”',
    },
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDataName(e.target.value.trim());
  };

  const handleBack = () => {
    history.push(`${SOURCE_WIZARD_BASE_URL}${CHOOSE_SOURCE_TYPE_URL}`);

    setSourceInfo({ ...sourceInfo, type: null });
  };

  const handleNext = async () => {
    let directoryId = null;
    if (sourceInfo.directoryId) {
      directoryId = sourceInfo.directoryId;
    } else {
      const directoryCreateRes = (await dispatch(createDirectory(dataName))) as any;
      directoryId = directoryCreateRes.body.id;
    }

    const sourceCreateRes = (await dispatch(
      createSource(directoryId, sourceInfo.type, dataName, sourceInfo.isPrimary),
    )) as any;
    const sourceId = sourceCreateRes.body.id;

    dispatch(initializeSource(sourceId));

    setSourceInfo({ ...sourceInfo, name: dataName, directoryId, sourceId });

    const baseUrl = makeSourceCreateBaseUrl(directoryId, sourceId);

    if (sourceInfo.type === CSV_SOURCE_TYPE_KEY) {
      history.push(`${baseUrl}${UPLOAD_CSV_URL}`);
    } else {
      history.push(`${baseUrl}${AUTHORIZE_DATA_ACCESS_URL}`);
    }

    analytics.track('Wizard - Naming Step Completed', {
      category: 'AD - Directories',
    });
  };

  return (
    <Base onBack={handleBack} onNext={handleNext} disableNextButton={!dataName}>
      <CardHeader title={data[dataType].title} subheader={data[dataType].description} />
      <div className={classes.inputContainer}>
        <TextField
          data-cy="data-name-input"
          label="Where is this data coming from?"
          placeholder={
            sourceInfo.isPrimary ? 'Name your Directory' : 'Name your Complementary Source'
          }
          onChange={handleChange}
        />
      </div>
    </Base>
  );
}

const useStyles = makeStyles(theme => ({
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(3),
    maxWidth: 366,
  },
}));

export default NameYourData;
